/**
 * Notifications service - fetching and actions for AT Protocol and Mastodon notifications
 */
import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { MastodonSession } from '@/lib/mastodon'
import type { PixelfedSession } from '@/lib/pixelfed'
import type {
    Notification,
    NotificationsPage,
    UnreadCountResult,
    MastodonNotification,
    MastodonMarkerResponse,
    FetchNotificationsOptions,
    ATNotificationReason,
} from '@/types/notifications'
import { mapATReasonToType, mapMastodonTypeToType } from '@/types/notifications'
import { isBridgedFromAP } from '@/lib/bridge'
import { isSafeHostname } from '@/lib/apUtils'
import { normalizeAcct } from '@/lib/mastodon'

// --- AT Protocol Notifications ---

/**
 * Fetch notifications for AT Protocol using the @atproto/api Agent
 */
export async function fetchATNotifications(
    session: OAuthSession,
    options?: FetchNotificationsOptions
): Promise<NotificationsPage> {
    const agent = new Agent(session)

    const response = await agent.app.bsky.notification.listNotifications({
        limit: options?.limit ?? 25,
        cursor: options?.cursor,
    })

    const notifications = response.data.notifications.map(mapATNotification)

    return {
        notifications,
        cursor: response.data.cursor,
        seenAt: response.data.seenAt,
    }
}

/**
 * Get unread count for AT Protocol
 */
export async function fetchATUnreadCount(session: OAuthSession): Promise<UnreadCountResult> {
    const agent = new Agent(session)

    const response = await agent.app.bsky.notification.getUnreadCount()

    return {
        count: response.data.count,
    }
}

/**
 * Mark notifications as seen for AT Protocol
 */
export async function markATNotificationsSeen(session: OAuthSession): Promise<void> {
    const agent = new Agent(session)

    await agent.app.bsky.notification.updateSeen({
        seenAt: new Date().toISOString(),
    })
}

/** AT Protocol notification shape from the API */
interface ATAPINotification {
    uri: string
    cid: string
    author: {
        did: string
        handle: string
        displayName?: string
        avatar?: string
        labels?: { val: string }[]
        viewer?: {
            following?: string
        }
    }
    reason: string
    reasonSubject?: string
    record: unknown
    isRead: boolean
    indexedAt: string
}

/**
 * Map AT Protocol notification to unified type
 */
function mapATNotification(notification: ATAPINotification): Notification {
    const text =
        notification.record && typeof notification.record === 'object'
            ? (((notification.record as Record<string, unknown>).text as string) ?? undefined)
            : undefined

    const reason = notification.reason as ATNotificationReason
    return {
        id: notification.uri,
        uri: notification.uri,
        type: mapATReasonToType(reason),
        atReason: reason,
        actor: {
            did: notification.author.did,
            handle: notification.author.handle,
            displayName: notification.author.displayName,
            avatarUrl: notification.author.avatar,
            isBridgedFromAP: isBridgedFromAP(notification.author.labels),
            viewerIsFollowing: Boolean(notification.author.viewer?.following),
        },
        // `subject.uri` mirrors AT Protocol's `reasonSubject` when present.
        // `groupNotifications` relies on this to match notifications by subject,
        // so any change to this mapping must keep groupable-reason subjects
        // stable across the same underlying `reasonSubject`.
        subject: notification.reasonSubject
            ? {
                  uri: notification.reasonSubject,
                  text,
              }
            : notification.reason === 'mention' || notification.reason === 'reply'
              ? {
                    uri: notification.uri,
                    text,
                }
              : undefined,
        createdAt: notification.indexedAt,
        isRead: notification.isRead,
        protocol: 'atproto',
    }
}

// --- Mastodon Helpers ---

/**
 * Validate that a Mastodon session's instance URL points to a safe external host.
 * Prevents SSRF if session storage is tampered with.
 */
function assertSafeInstanceUrl(mastodonSession: MastodonSession | PixelfedSession): void {
    let hostname: string
    try {
        hostname = new URL(mastodonSession.instance_url).hostname
    } catch {
        throw new Error('Invalid Mastodon instance URL')
    }
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe Mastodon instance URL: ${mastodonSession.instance_url}`)
    }
}

/**
 * Safely convert a string to BigInt, returning null on failure.
 * Mastodon IDs are numeric strings but we can't trust external API responses.
 * Rejects empty strings (BigInt('') === 0n per spec, but '' is not a valid ID).
 */
export function safeBigInt(value: string): bigint | null {
    if (value === '') return null
    try {
        return BigInt(value)
    } catch {
        return null
    }
}

// --- Local Marker Fallback ---
// Not all instances support the Mastodon markers API (e.g. Pixelfed).
// Persist last-read ID in localStorage as a fallback.

const LOCAL_MARKER_KEY = 'mackinac_notification_markers'

function getLocalMarker(instanceUrl: string): string | undefined {
    try {
        const stored = localStorage.getItem(LOCAL_MARKER_KEY)
        if (!stored) return undefined
        const markers: Record<string, string> = JSON.parse(stored)
        return markers[instanceUrl]
    } catch {
        return undefined
    }
}

function setLocalMarker(instanceUrl: string, lastReadId: string): void {
    try {
        const stored = localStorage.getItem(LOCAL_MARKER_KEY)
        const markers: Record<string, string> = stored ? JSON.parse(stored) : {}
        markers[instanceUrl] = lastReadId
        localStorage.setItem(LOCAL_MARKER_KEY, JSON.stringify(markers))
    } catch {
        // Ignore storage errors
    }
}

// --- Mastodon Markers API ---

/**
 * Fetch the Mastodon notification marker (last_read_id).
 * Falls back to localStorage if the server doesn't support markers.
 */
export async function fetchMastodonMarker(
    mastodonSession: MastodonSession | PixelfedSession
): Promise<MastodonMarkerResponse> {
    assertSafeInstanceUrl(mastodonSession)

    // Try server-side markers API
    try {
        const url = new URL(`${mastodonSession.instance_url}/api/v1/markers`)
        url.searchParams.set('timeline[]', 'notifications')

        const response = await fetch(url.toString(), {
            headers: {
                Authorization: `Bearer ${mastodonSession.access_token}`,
            },
        })

        if (response.ok) {
            const data: MastodonMarkerResponse = await response.json()
            // Only use API response if it actually contains a marker
            if (data.notifications?.last_read_id) {
                return data
            }
        }
    } catch {
        // Fall through to local fallback
    }

    // Fall back to localStorage (API missing, failed, or returned empty marker)
    const localId = getLocalMarker(mastodonSession.instance_url)
    if (localId) {
        return {
            notifications: {
                last_read_id: localId,
                version: 0,
                updated_at: new Date().toISOString(),
            },
        }
    }

    return {}
}

/**
 * Update the Mastodon notification marker to mark notifications as read.
 * Always persists locally; also tries the server-side API.
 */
export async function updateMastodonMarker(
    mastodonSession: MastodonSession | PixelfedSession,
    lastReadId: string
): Promise<MastodonMarkerResponse> {
    assertSafeInstanceUrl(mastodonSession)
    if (!/^\d+$/.test(lastReadId)) {
        throw new Error('Invalid notification ID for marker update')
    }

    // Always persist locally as a fallback
    setLocalMarker(mastodonSession.instance_url, lastReadId)

    // Try server-side markers API
    try {
        const url = new URL(`${mastodonSession.instance_url}/api/v1/markers`)
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${mastodonSession.access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                notifications: { last_read_id: lastReadId },
            }),
        })

        if (response.ok) {
            return response.json()
        }
    } catch {
        // Instance doesn't support markers API — local fallback is sufficient
    }

    return {
        notifications: {
            last_read_id: lastReadId,
            version: 0,
            updated_at: new Date().toISOString(),
        },
    }
}

// --- Mastodon Notifications ---

/**
 * Fetch notifications for Mastodon
 */
export async function fetchMastodonNotifications(
    mastodonSession: MastodonSession | PixelfedSession,
    options?: FetchNotificationsOptions,
    sourceProtocol: 'mastodon' | 'pixelfed' = 'mastodon'
): Promise<NotificationsPage> {
    assertSafeInstanceUrl(mastodonSession)
    const url = new URL(`${mastodonSession.instance_url}/api/v1/notifications`)
    url.searchParams.set('limit', String(options?.limit ?? 25))
    if (options?.cursor) {
        url.searchParams.set('max_id', options.cursor)
    }

    const response = await fetch(url.toString(), {
        headers: {
            Authorization: `Bearer ${mastodonSession.access_token}`,
        },
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch Mastodon notifications: ${response.status}`)
    }

    const data: MastodonNotification[] = await response.json()

    // Get cursor from Link header for pagination
    const linkHeader = response.headers.get('Link')
    const cursor = parseLinkHeader(linkHeader)

    const notifications = data.map(n =>
        mapMastodonNotification(n, sourceProtocol, mastodonSession.instance_url)
    )

    return {
        notifications,
        cursor,
    }
}

/**
 * Get unread count for Mastodon using the markers API.
 * Fetches the notification marker and recent notifications,
 * then counts notifications with id > last_read_id.
 */
export async function fetchMastodonUnreadCount(
    mastodonSession: MastodonSession | PixelfedSession
): Promise<UnreadCountResult> {
    assertSafeInstanceUrl(mastodonSession)
    const notificationsUrl = new URL(`${mastodonSession.instance_url}/api/v1/notifications`)
    notificationsUrl.searchParams.set('limit', '40')

    // Fetch marker and recent notifications in parallel
    const [markerResponse, notificationsResponse] = await Promise.all([
        fetchMastodonMarker(mastodonSession).catch(() => null),
        fetch(notificationsUrl.toString(), {
            headers: {
                Authorization: `Bearer ${mastodonSession.access_token}`,
            },
        }),
    ])

    if (!notificationsResponse.ok) {
        throw new Error(`Failed to fetch Mastodon notifications: ${notificationsResponse.status}`)
    }

    const data: MastodonNotification[] = await notificationsResponse.json()
    const lastReadId = markerResponse?.notifications?.last_read_id

    if (!lastReadId) {
        // No marker set yet — all notifications are unread
        return { count: data.length }
    }

    const count = countUnreadNotifications(data, lastReadId)
    return { count }
}

/**
 * Map Mastodon notification to unified type.
 * Read state is not set here — it is applied as a presentation-layer
 * annotation in useNotifications using the marker's lastReadId.
 *
 * @param instanceUrl - The session's instance URL, used to normalize local actor handles
 *   (e.g. "alice" → "alice@pixelfed.social"). Pass when available.
 */
export function mapMastodonNotification(
    notification: MastodonNotification,
    sourceProtocol: 'mastodon' | 'pixelfed' = 'mastodon',
    instanceUrl?: string
): Notification {
    const actorHandle = instanceUrl
        ? normalizeAcct(notification.account.acct, instanceUrl)
        : notification.account.acct

    return {
        id: notification.id,
        uri: notification.status?.uri ?? `mastodon:notification:${notification.id}`,
        type: mapMastodonTypeToType(notification.type),
        mastodonType: notification.type,
        actor: {
            handle: actorHandle,
            displayName: notification.account.display_name || notification.account.username,
            avatarUrl: notification.account.avatar,
        },
        subject: notification.status
            ? {
                  uri: notification.status.uri,
                  text: stripHtml(notification.status.content),
                  createdAt: notification.status.created_at,
              }
            : undefined,
        createdAt: notification.created_at,
        isRead: false,
        protocol: sourceProtocol,
    }
}

// --- Unified Fetch Functions ---

/**
 * Fetch notifications for the specified protocol
 */
export async function fetchNotifications(
    protocol: 'atproto' | 'mastodon',
    sessions: {
        atSession?: OAuthSession | null
        mastodonSession?: MastodonSession | PixelfedSession | null
    },
    options?: FetchNotificationsOptions,
    sourceProtocol?: 'mastodon' | 'pixelfed'
): Promise<NotificationsPage> {
    if (protocol === 'atproto') {
        if (!sessions.atSession) {
            throw new Error('AT Protocol session required')
        }
        return fetchATNotifications(sessions.atSession, options)
    } else {
        if (!sessions.mastodonSession) {
            throw new Error('Mastodon session required')
        }
        return fetchMastodonNotifications(
            sessions.mastodonSession,
            options,
            sourceProtocol ?? 'mastodon'
        )
    }
}

/**
 * Fetch unread count for the specified protocol
 */
export async function fetchUnreadCount(
    protocol: 'atproto' | 'mastodon',
    sessions: {
        atSession?: OAuthSession | null
        mastodonSession?: MastodonSession | PixelfedSession | null
    }
): Promise<UnreadCountResult> {
    if (protocol === 'atproto') {
        if (!sessions.atSession) {
            return { count: 0 }
        }
        return fetchATUnreadCount(sessions.atSession)
    } else {
        if (!sessions.mastodonSession) {
            return { count: 0 }
        }
        return fetchMastodonUnreadCount(sessions.mastodonSession)
    }
}

// --- Helpers ---

/**
 * Count notifications newer than the last read marker.
 * Uses BigInt for safe comparison of Mastodon's numeric string IDs.
 */
export function countUnreadNotifications(
    notifications: MastodonNotification[],
    lastReadId: string
): number {
    const lastRead = safeBigInt(lastReadId)
    if (lastRead === null) return 0
    return notifications.filter(n => {
        const nId = safeBigInt(n.id)
        return nId !== null && nId > lastRead
    }).length
}

/**
 * Parse Link header to extract pagination cursor
 */
export function parseLinkHeader(linkHeader: string | null): string | undefined {
    if (!linkHeader) return undefined

    // Look for next page link: <url>; rel="next"
    const nextMatch = linkHeader.match(/<[^>]*[?&]max_id=([^&>]+)[^>]*>;\s*rel="next"/)
    return nextMatch?.[1]
}

/**
 * Strip HTML tags from content safely using DOMParser
 * This prevents XSS attacks by using the browser's built-in HTML parser
 */
export function stripHtml(html: string): string {
    // Use browser's built-in HTML parser (safe and reliable)
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const text = doc.body.textContent || ''

    // Clean up whitespace
    return text.replace(/\n\s*\n/g, '\n').trim()
}
